/* Yeni bloklar — ekleme menüsünün kataloğu ve her tipin başlangıç hâli.

   Başlangıç metinleri yer tutucu değil, gerçek ama kısa: boş bir başlık
   sayfada görünmez olur ve yönetici nereye yazacağını bulamaz. İlk tıklamada
   seçiliyor, üzerine yazılıyor. */

import { newId, type Block, type BlockType } from '@frawgraphy/shared';

export type CatalogEntry = { type: BlockType; label: string; hint: string; group: 'Metin' | 'Düzen' | 'Görsel' | 'Koleksiyon'; topLevel?: boolean };

export const CATALOG: CatalogEntry[] = [
  { type: 'paragraph', label: 'Paragraf', hint: 'Düz metin', group: 'Metin' },
  { type: 'heading', label: 'Başlık', hint: 'Bölüm ya da ara başlık', group: 'Metin' },
  { type: 'eyebrow', label: 'Üst etiket', hint: 'Başlığın üstündeki küçük satır', group: 'Metin' },
  { type: 'quote', label: 'Alıntı', hint: 'Büyük italik alıntı ve kaynağı', group: 'Metin' },
  { type: 'list', label: 'Liste', hint: 'Madde ya da numaralı', group: 'Metin' },
  { type: 'table', label: 'Tablo', hint: 'Satır ve sütun', group: 'Metin' },
  { type: 'note', label: 'Not', hint: 'Simgeli açıklama satırı', group: 'Metin' },
  { type: 'section', label: 'Bölüm', hint: 'Başlıklı yeni sayfa bölümü', group: 'Düzen', topLevel: true },
  { type: 'columns', label: 'Sütunlar', hint: 'Yan yana 2–4 sütun', group: 'Düzen' },
  { type: 'buttons', label: 'Düğme', hint: 'Bağlantılı düğmeler', group: 'Düzen' },
  { type: 'divider', label: 'Ayırıcı çizgi', hint: '', group: 'Düzen' },
  { type: 'spacer', label: 'Boşluk', hint: 'Dikey boşluk', group: 'Düzen' },
  { type: 'image', label: 'Görsel', hint: 'Arşivden bir kare', group: 'Görsel' },
  { type: 'gallery', label: 'Galeri', hint: 'Kare ızgarası', group: 'Görsel' },
  { type: 'cards', label: 'Kartlar', hint: 'Görselli kart ızgarası', group: 'Koleksiyon' },
  { type: 'facts', label: 'Bilgi listesi', hint: 'Etiket — değer satırları', group: 'Koleksiyon' }
];

export const TYPE_LABELS: Record<BlockType, string> = {
  heading: 'Başlık',
  paragraph: 'Paragraf',
  eyebrow: 'Üst etiket',
  note: 'Not',
  quote: 'Alıntı',
  list: 'Liste',
  table: 'Tablo',
  buttons: 'Düğmeler',
  divider: 'Ayırıcı',
  spacer: 'Boşluk',
  image: 'Görsel',
  gallery: 'Galeri',
  columns: 'Sütunlar',
  cards: 'Kartlar',
  reels: 'Video kartları',
  facts: 'Bilgi listesi',
  split: 'Görsel + metin',
  cta: 'Çağrı',
  coverflow: 'Seçili işler',
  contact: 'İletişim formu',
  hero: 'Giriş (hero)',
  section: 'Bölüm',
  html: 'Eski içerik',
  page: 'Sayfa bölgesi',
  pagecta: 'Yönlendirme kutusu',
  nav: 'Menü',
  footer: 'Footer'
};

/** `type` tipinde yeni bir blok; kimlikleri `taken`'da olmayan. */
export function blankBlock(type: BlockType, taken: Set<string>): Block {
  const id = (prefix: string) => {
    const v = newId(prefix, taken);
    taken.add(v);
    return v;
  };
  switch (type) {
    case 'paragraph':
      return { id: id('paragraf'), type, text: { tr: 'Yeni paragraf.' } };
    case 'heading':
      return { id: id('baslik'), type, level: 2, text: { tr: 'Yeni başlık' } };
    case 'eyebrow':
      return { id: id('etiket'), type, text: { tr: 'Üst etiket' } };
    case 'note':
      return { id: id('not'), type, text: { tr: 'Açıklama.' } };
    case 'quote':
      return { id: id('alinti'), type, text: { tr: 'Alıntı metni.' }, cite: { tr: 'Kaynak' } };
    case 'list':
      return { id: id('liste'), type, ordered: false, items: [{ text: { tr: 'Birinci madde' } }, { text: { tr: 'İkinci madde' } }] };
    case 'table':
      return {
        id: id('tablo'),
        type,
        header: true,
        rows: [
          [{ tr: 'Başlık' }, { tr: 'Başlık' }],
          [{ tr: 'Hücre' }, { tr: 'Hücre' }]
        ]
      };
    case 'buttons':
      return { id: id('dugme'), type, items: [{ id: id('btn'), label: { tr: 'Bize yazın' }, href: '#contact', variant: 'primary', arrow: true }] };
    case 'divider':
      return { id: id('cizgi'), type };
    case 'spacer':
      return { id: id('bosluk'), type, size: 'm' };
    case 'image':
      return { id: id('gorsel'), type, media: { src: 'assets/img/studio-1100.jpg', alt: { tr: '' }, width: 1100, height: 1374 }, ratio: 'auto' };
    case 'gallery':
      return {
        id: id('galeri'),
        type,
        cols: 3,
        items: ['work-1', 'work-2', 'work-3'].map((p) => ({ id: id('kare'), src: `assets/img/${p}-900.jpg`, alt: { tr: '' } }))
      };
    case 'columns':
      return {
        id: id('sutunlar'),
        type,
        columns: [
          { id: id('sutun'), blocks: [{ id: id('paragraf'), type: 'paragraph', text: { tr: 'Sol sütun.' } }] },
          { id: id('sutun'), blocks: [{ id: id('paragraf'), type: 'paragraph', text: { tr: 'Sağ sütun.' } }] }
        ]
      };
    case 'cards':
      return {
        id: id('kartlar'),
        type,
        variant: 'plain',
        items: [1, 2, 3].map((n) => ({ id: id('kart'), title: { tr: `Kart ${n}` }, desc: { tr: 'Kısa açıklama' } }))
      };
    case 'facts':
      return {
        id: id('bilgi'),
        type,
        variant: 'plain',
        items: [
          { id: id('satir'), label: { tr: 'Etiket' }, value: { tr: 'Değer' } },
          { id: id('satir'), label: { tr: 'Etiket' }, value: { tr: 'Değer' } }
        ]
      };
    case 'section': {
      const anchor = id('bolum');
      return {
        id: anchor,
        type,
        anchor,
        alt: false,
        head: [
          { id: id('etiket'), type: 'eyebrow', text: { tr: 'Yeni bölüm' } },
          { id: id('baslik'), type: 'heading', level: 2, text: { tr: 'Bölüm <em>başlığı</em>' } }
        ],
        blocks: [{ id: id('paragraf'), type: 'paragraph', text: { tr: 'Bölümün metni.' } }]
      };
    }
    default:
      throw new Error(`Bu tip menüden eklenmez: ${type}`);
  }
}

/** Kart, satır ya da kare için yeni öğe — "+ kart" düğmesi. */
export function blankItem(block: Block, taken: Set<string>): ({ id: string } & Record<string, unknown>) | null {
  const id = newId('item', taken);
  taken.add(id);
  switch (block.type) {
    case 'cards':
      return block.variant === 'service'
        ? { id, title: { tr: 'Yeni hizmet' }, desc: { tr: 'Açıklama' }, meta: { tr: 'Süre · teslim' } }
        : block.variant === 'ai'
          ? { id, title: { tr: 'Yeni çalışma' }, desc: { tr: 'Açıklama' }, category: { tr: 'Kavram' }, year: String(new Date().getFullYear()), media: { src: 'assets/img/ai-1-900.jpg', alt: { tr: '' }, width: 900, height: 1205 }, full: 'assets/img/ai-1-full.jpg' }
          : { id, title: { tr: 'Yeni kart' }, desc: { tr: 'Kısa açıklama' } };
    case 'facts':
    case 'contact':
      return { id, label: { tr: 'Etiket' }, value: { tr: 'Değer' } };
    case 'reels':
      return { id, title: { tr: 'Yeni film' }, spec: { tr: '16:9' }, note: { tr: 'Açıklama.' }, time: '00:30', poster: 'https://picsum.photos/id/1011/1280/720?grayscale', video: 'assets/video/yeni.mp4', focus: '50% 50%', label: { tr: 'Film' } };
    case 'gallery':
      return { id, src: 'assets/img/work-1-900.jpg', alt: { tr: '' } };
    case 'buttons':
    case 'pagecta':
      return { id, label: { tr: 'Yeni düğme' }, href: '#contact', variant: block.type === 'pagecta' ? 'plain' : 'ghost' };
    case 'nav':
    case 'footer':
      return { id, label: { tr: 'Yeni bağlantı' }, href: '#contact' };
    case 'columns':
      return { id, blocks: [{ id: newId('paragraf', taken), type: 'paragraph', text: { tr: 'Yeni sütun.' } }] };
    default:
      return null;
  }
}
